import { useState, useEffect, useCallback } from 'react';
import { Project, Task, Milestone, MilestoneDeliverable } from '../types';
import { usePermissions } from '../services/PermissionService';
import { useAuth } from '../contexts/AuthContext';
import { isPlatformRole, resolveUserMvpAccountRole } from '../lib/auth/accountRoles';
import {
  getAccessibleResourceIds,
  getInvestmentDerivedProjectIds,
  getScopedAccessibleProjectIds,
  getScopedCapabilitiesForResource,
} from '../lib/auth/scopedRelationshipResolver';
import {
  createShowcaseMilestoneDeliverables,
  createShowcaseMilestones,
  createShowcaseProjects,
  createShowcaseTasks,
  resolveShowcaseProjectIdsForInvestment,
} from '../demo/showcaseCatalog';
import {
  createBootstrapMilestoneId,
  createBootstrapProjectId,
  createBootstrapTaskId,
  getBootstrapProjectSnapshot,
  listBootstrapMilestones,
  listBootstrapProjects,
  listBootstrapTasks,
  removeBootstrapMilestone,
  removeBootstrapTask,
  saveBootstrapMilestone,
  saveBootstrapProject,
  saveBootstrapTask,
} from '../services/projects/BootstrapProjectPersistence';
import { IS_DEMO_MODE } from '../config/env';
import { productionProjectPersistence } from '../services/projects/ProductionProjectPersistenceService';

// One coherent showcase powers every demo-facing project surface.
const demoProjects: Project[] = createShowcaseProjects();
const demoTasks: Task[] = createShowcaseTasks();
const demoMilestones: Milestone[] = createShowcaseMilestones();
const demoMilestoneDeliverables: Record<string, MilestoneDeliverable[]> =
  createShowcaseMilestoneDeliverables();

// ─────────────────────────────────────────────────────────────────────────────
// Shared (module-scope) stores
// ─────────────────────────────────────────────────────────────────────────────
// `useProjects()` is consumed from many independent components (Milestones
// page, Governance Journey page, Project Detail, drawers, etc.). Backing the
// data with per-component `useState` produced per-instance islands: a
// milestone created by one consumer was invisible to all others until a hard
// reload. Lifting the four mutable buckets to module-scope with a tiny
// pub/sub gives every `useProjects()` caller a consistent live view without
// changing the hook's public surface — `setX` calls inside the hook still
// work as before.
type _SharedSetter<T> = (updater: T | ((prev: T) => T)) => void;

function _createSharedStore<T>(initial: T): {
  get: () => T;
  set: _SharedSetter<T>;
  useShared: () => [T, _SharedSetter<T>];
} {
  let state: T = initial;
  const listeners = new Set<() => void>();
  const set: _SharedSetter<T> = (updater) => {
    state = typeof updater === 'function'
      ? (updater as (prev: T) => T)(state)
      : updater;
    listeners.forEach((l) => l());
  };
  return {
    get: () => state,
    set,
    useShared: () => {
      const [, force] = useState(0);
      useEffect(() => {
        const listener = () => force((v) => v + 1);
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      }, []);
      return [state, set];
    },
  };
}

const _projectsStore = _createSharedStore<Project[]>(IS_DEMO_MODE
  ? [
    ...demoProjects,
    ...listBootstrapProjects().filter(
      (project) => !demoProjects.some((showcase) => showcase.id === project.id),
    ),
  ]
  : []);
const _tasksStore = _createSharedStore<Task[]>(IS_DEMO_MODE
  ? [
    ...demoTasks,
    ...listBootstrapTasks().filter(
      (task) => !demoTasks.some((showcase) => showcase.id === task.id),
    ),
  ]
  : []);
const _milestonesStore = _createSharedStore<Milestone[]>(IS_DEMO_MODE
  ? [
    ...demoMilestones,
    ...listBootstrapMilestones().filter(
      (milestone) => !demoMilestones.some((showcase) => showcase.id === milestone.id),
    ),
  ]
  : []);
const _milestoneDeliverablesStore = _createSharedStore<Record<string, MilestoneDeliverable[]>>(
  IS_DEMO_MODE ? demoMilestoneDeliverables : {},
);

const _showcaseProjectIds = new Set(demoProjects.map((project) => project.id));

/** Read-only bridge used by the Project detail loader for runtime-created demo records. */
export function getSharedProjectSnapshot(projectId: string): {
  project: Project | null;
  tasks: Task[];
  milestones: Milestone[];
} {
  const runtime = getBootstrapProjectSnapshot(projectId);
  const project = _projectsStore.get().find((candidate) => candidate.id === projectId)
    ?? runtime.project;
  return {
    project,
    tasks: _tasksStore.get().filter((task) => task.projectId === projectId).length > 0
      ? _tasksStore.get().filter((task) => task.projectId === projectId)
      : runtime.tasks,
    milestones: _milestonesStore.get().filter((milestone) => milestone.projectId === projectId).length > 0
      ? _milestonesStore.get().filter((milestone) => milestone.projectId === projectId)
      : runtime.milestones,
  };
}

/** Keeps route-local edits and the persistent bootstrap record in one canonical snapshot. */
export function updateSharedProjectSnapshot(project: Project): void {
  if (!_showcaseProjectIds.has(project.id)) saveBootstrapProject(project);
  _projectsStore.set((current) => current.map((entry) => entry.id === project.id ? project : entry));
}

export function mergeVisibleProjectsWithRuntime(
  visibleProjects: Project[],
  currentProjects: Project[],
): Project[] {
  const visibleIds = new Set(visibleProjects.map((project) => project.id));
  const runtimeProjects = currentProjects.filter(
    (project) => !_showcaseProjectIds.has(project.id) && !visibleIds.has(project.id),
  );
  return [...visibleProjects, ...runtimeProjects];
}

export function useProjects() {
  const { checkPermission } = usePermissions();
  // We need to import useAuth for user information
  const { user } = useAuth();
  // Shared (module-level) state: every `useProjects()` call subscribes to the
  // SAME projects/tasks/milestones/deliverables buckets. Previously each call
  // site had its own `useState`-isolated copy, which meant a milestone created
  // from the Milestones page was invisible to the Governance Journey page
  // (and to every other consumer) — they'd each start fresh from `demoX` and
  // never see mutations made elsewhere. The `setX` callsites below keep the
  // exact same shape, so no other code in this file needs to change.
  const [projects, setProjects] = _projectsStore.useShared();
  const [tasks, setTasks] = _tasksStore.useShared();
  const [milestones, setMilestones] = _milestonesStore.useShared();
  const [milestoneDeliverables, setMilestoneDeliverables] = _milestoneDeliverablesStore.useShared();
  const [loading, setLoading] = useState(!IS_DEMO_MODE);
  const [error, setError] = useState<string | null>(null);
  // Manual refresh trigger — increment to re-run the data-load effect
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const refetch = useCallback(() => setRefetchTrigger(t => t + 1), []);

  // ─── Listen for tasks created from ChatHub (for live updates) ──────────────
  // The global listener persists tasks, but we also need to update local state
  useEffect(() => {
    const handleTaskCreatedFromChat = (event: Event) => {
      const customEvent = event as CustomEvent<{ task: Task }>;
      const { task } = customEvent.detail;

      if (task) {
        console.log('[useProjects] 📥 Live update - Task from ChatHub:', task.id);
        setTasks(prev => {
          // Prevent duplicates
          if (prev.some(t => t.id === task.id)) {
            return prev;
          }
          return [...prev, task];
        });
      }
    };

    window.addEventListener('ax1:task-created-from-chathub', handleTaskCreatedFromChat);
    return () => window.removeEventListener('ax1:task-created-from-chathub', handleTaskCreatedFromChat);
  }, [setTasks]);

  useEffect(() => {
    if (!IS_DEMO_MODE) {
      let cancelled = false;
      setLoading(true);
      setError(null);

      if (!user || !productionProjectPersistence) {
        if (user && !productionProjectPersistence) setError('Project data is not configured.');
        setProjects([]);
        setTasks([]);
        setMilestones([]);
        setMilestoneDeliverables({});
        setLoading(false);
        return () => {
          cancelled = true;
        };
      }

      void productionProjectPersistence
        .listProjectSnapshots()
        .then((snapshots) => {
          if (cancelled) return;
          setProjects(snapshots.map((snapshot) => snapshot.project));
          setTasks(snapshots.flatMap((snapshot) => snapshot.tasks));
          setMilestones(snapshots.flatMap((snapshot) => snapshot.milestones));
          setMilestoneDeliverables({});
        })
        .catch((error) => {
          if (cancelled) return;
          console.error('[useProjects] Failed to load authoritative Project records:', error);
          setError('Project data could not be loaded.');
          setProjects([]);
          setTasks([]);
          setMilestones([]);
          setMilestoneDeliverables({});
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }

    // Simulate loading
    const loadData = () => {
      if (user) {
        // Filter projects based on user role and permissions
        let filteredProjects = demoProjects;
        let filteredTasks = demoTasks;

        // Normal project visibility must never come from account role alone.
        // Canonical organization-administration policy: only the canonical MVP
        // account roles `organization_owner`/`organization_admin` WITH an active
        // workspace membership receive full workspace project visibility. This
        // is an explicit account-role policy, not a legacy alias — the legacy
        // TOKEN_OPS/TOKEN_WS workspace membership strings never
        // authorize here. Everyone else sees only scoped-relationship-derived
        // projects.
        const mvpRole = resolveUserMvpAccountRole(user);
        const isPlatformAccount = mvpRole === 'platform_owner'
          || mvpRole === 'platform_admin'
          || mvpRole === 'support_operator'
          || isPlatformRole(user.platform_role);
        const hasActiveWorkspaceMembership = user.status === 'active' && Boolean(user.workspace_id);
        const hasOrgWideProjectVisibility = hasActiveWorkspaceMembership
          && (mvpRole === 'organization_owner' || mvpRole === 'organization_admin');

        if (isPlatformAccount) {
          // Platform/control-plane roles never get standing customer project
          // access; that would require JIT elevation, not covered here.
          filteredProjects = [];
          filteredTasks = [];
        } else if (hasOrgWideProjectVisibility) {
          // Canonical organization-admin visibility policy (with membership).
          filteredProjects = demoProjects;
          filteredTasks = demoTasks;
        } else if (mvpRole === 'organization_member') {
          // Ordinary members only see projects backed by an active,
          // sufficiently-privileged scoped relationship. Direct project/company
          // relationships grant project access directly; investor relationships
          // grant it only through the authoritative investment→project link
          // (never a project id attached to the investor relationship itself).
          const directProjectIds = getScopedAccessibleProjectIds(user, 'view');
          const investmentDerivedProjectIds = getInvestmentDerivedProjectIds(
            user,
            resolveShowcaseProjectIdsForInvestment,
            'view',
          );
          const accessibleProjectIds = new Set<string>([
            ...directProjectIds,
            ...investmentDerivedProjectIds,
          ]);
          // Exact-object assignments are discovery grants for those objects,
          // not implicit membership of the parent Project. Keep them out of
          // `filteredProjects` while allowing the assigned Task to reach the
          // canonical Tasks surface.
          const exactTaskIds = getAccessibleResourceIds(user, 'task', 'view');
          filteredProjects = demoProjects.filter(p => accessibleProjectIds.has(p.id));
          filteredTasks = demoTasks.filter(t => {
            if (exactTaskIds.has(t.id)) return true;
            if (!accessibleProjectIds.has(t.projectId)) return false;
            // A relationship carrying `tasks.view` on this project (e.g. project_manager,
            // partner_representative) sees all project tasks; otherwise only tasks
            // assigned to this user are visible.
            const projectCapabilities = getScopedCapabilitiesForResource(user, 'project', t.projectId);
            return projectCapabilities.includes('tasks.view') || t.assigneeId === user.id;
          });
        } else {
          // Unknown/unresolvable role - fail closed.
          filteredProjects = [];
          filteredTasks = [];
        }

        // Connect tasks to milestones. Only rebuild the slice that still
        // matches demo IDs — preserve any milestones that were created
        // at runtime (i.e., not present in demoMilestones).
        const demoMilestoneIds = new Set(demoMilestones.map(m => m.id));
        const milestonesWithTasks = demoMilestones.map(milestone => {
          const milestoneTasks = filteredTasks.filter(task => task.milestoneId === milestone.id);
          return {
            ...milestone,
            tasks: milestoneTasks
          };
        });

        // A role refresh must not erase projects created during this session.
        // The catalog slice is replaced; runtime records are preserved.
        setProjects(prev => mergeVisibleProjectsWithRuntime(filteredProjects, prev));
        // Preserve tasks created during this browser session while refreshing
        // the catalog-backed task slice.
        const showcaseTaskIds = new Set(demoTasks.map(task => task.id));
        setTasks(prev => [
          ...filteredTasks,
          ...prev.filter(task => !showcaseTaskIds.has(task.id) && !filteredTasks.some(seed => seed.id === task.id)),
        ]);
        // Preserve runtime-created milestones (non-demo IDs) while refreshing
        // the demo-seeded ones. Without this guard, every new `useProjects()`
        // mount (e.g., the Governance Journey page) re-runs this effect and
        // overwrites the shared store back to demoMilestones, wiping any
        // milestone the user just created.
        setMilestones(prev => {
          const runtimeCreated = prev.filter(m => !demoMilestoneIds.has(m.id));
          return [...milestonesWithTasks, ...runtimeCreated];
        });
        setMilestoneDeliverables(demoMilestoneDeliverables);
      }
      setLoading(false);
    };

    // Load data immediately if user is available, otherwise wait
    if (user) {
      loadData();
    } else {
      // Set a short timeout to wait for user context to initialize
      const timer = setTimeout(() => {
        loadData();
      }, 100);
      return () => clearTimeout(timer);
    }
  // refetchTrigger is intentionally included so callers can force a reload
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refetchTrigger]);

  const createProject = (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!checkPermission('projects.create')) {
      throw new Error('Insufficient permissions to create projects');
    }

    const newProject: Project = {
      ...project,
      id: createBootstrapProjectId(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    saveBootstrapProject(newProject);
    setProjects(prev => [...prev, newProject]);

    return newProject;
  };

  const updateProject = (id: string, updates: Partial<Project>) => {
    if (!checkPermission('projects.update')) {
      throw new Error('Insufficient permissions to update projects');
    }

    setProjects(prev => prev.map(p => {
      if (p.id !== id) return p;
      const updated = { ...p, ...updates, updatedAt: new Date() };
      if (!_showcaseProjectIds.has(id)) saveBootstrapProject(updated);
      return updated;
    }));
  };

  const createTask = (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!checkPermission('tasks.create')) {
      throw new Error('Insufficient permissions to create tasks');
    }

    // Set default visibility to internal if not specified
    const newTask: Task = {
      ...task,
      id: createBootstrapTaskId(),
      visibility: task.visibility || 'internal', // Default to internal visibility
      createdAt: new Date(),
      updatedAt: new Date()
    };
    saveBootstrapTask(newTask);
    setTasks(prev => [...prev, newTask]);
    return newTask;
  };

  const updateTask = (id: string, updates: Partial<Task>) => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;

    // Check permissions based on role
    if (checkPermission('tasks.update')) {
      // Full update permissions (Admin, Project Manager)
    } else if (checkPermission('tasks.update.assigned') && task.assigneeId === user?.id) {
      // Can only update assigned tasks (Internal Team Member)
    } else {
      throw new Error('Insufficient permissions to update this task');
    }

    if (import.meta.env.DEV) console.log('🔄 [useProjects] updateTask - before update:', { id, updates });
    if (import.meta.env.DEV) console.log('🔄 [useProjects] Current tasks:', tasks.map(t => ({ id: t.id, status: t.status })));

    setTasks(prev => {
      const newTasks = prev.map(t => {
        if (t.id !== id) return t;
        const updated = { ...t, ...updates, updatedAt: new Date() };
        if (!demoTasks.some((showcase) => showcase.id === id)) saveBootstrapTask(updated);
        return updated;
      });
      if (import.meta.env.DEV) console.log('🔄 [useProjects] Tasks after update:', newTasks.map(t => ({ id: t.id, status: t.status })));
      return newTasks;
    });

    // Update milestone tasks if this task is linked to a milestone
    if (task.milestoneId) {
      setMilestones(prev => prev.map(m => {
        if (m.id === task.milestoneId) {
          const updatedTasks = m.tasks.map(t =>
            t.id === id ? { ...t, ...updates, updatedAt: new Date() } : t
          );
          return { ...m, tasks: updatedTasks };
        }
        return m;
      }));
    }

    // Log the update for debugging
    if (import.meta.env.DEV) console.log(`Task ${id} updated:`, updates);
  };

  const createMilestone = (milestone: Omit<Milestone, 'id' | 'createdAt'>) => {
    if (!checkPermission('projects.update')) {
      throw new Error('Insufficient permissions to create milestones');
    }

    const newMilestone: Milestone = {
      ...milestone,
      id: createBootstrapMilestoneId(),
      createdAt: new Date(),
      tasks: [] // Initialize with empty tasks array
    };

    saveBootstrapMilestone(newMilestone);
    setMilestones(prev => [...prev, newMilestone]);

    return newMilestone;
  };

  const updateMilestone = (id: string, updates: Partial<Milestone>) => {
    if (!checkPermission('projects.update')) {
      throw new Error('Insufficient permissions to update milestones');
    }

    setMilestones(prev => prev.map(m => {
      if (m.id !== id) return m;
      const updated = { ...m, ...updates, updatedAt: new Date() };
      if (!demoMilestones.some((showcase) => showcase.id === id)) saveBootstrapMilestone(updated);
      return updated;
    }));
  };

  const deleteMilestone = (id: string) => {
    if (!checkPermission('projects.update')) {
      throw new Error('Insufficient permissions to delete milestones');
    }

    removeBootstrapMilestone(id);
    setMilestones(prev => prev.filter(m => m.id !== id));

    // Unlink tasks from this milestone
    setTasks(prev => prev.map(t =>
      t.milestoneId === id ? { ...t, milestoneId: undefined, updatedAt: new Date() } : t
    ));
  };

  const getMilestoneDeliverables = (_milestoneId: string): MilestoneDeliverable[] => {
    return milestoneDeliverables[_milestoneId] || [];
  };

  const addMilestoneDeliverable = (milestoneId: string, deliverable: Omit<MilestoneDeliverable, 'id' | 'createdAt'>) => {
    if (!checkPermission('projects.update')) {
      throw new Error('Insufficient permissions to add deliverables');
    }

    const newDeliverable: MilestoneDeliverable = {
      ...deliverable,
      id: Date.now().toString(),
      createdAt: new Date()
    };

    setMilestoneDeliverables(prev => {
      const currentDeliverables = prev[milestoneId] || [];
      return {
        ...prev,
        [milestoneId]: [...currentDeliverables, newDeliverable]
      };
    });

    return newDeliverable;
  };

  const updateMilestoneDeliverable = (milestoneId: string, deliverableId: string, updates: Partial<MilestoneDeliverable>) => {
    if (!checkPermission('projects.update')) {
      throw new Error('Insufficient permissions to update deliverables');
    }

    setMilestoneDeliverables(prev => {
      const currentDeliverables = prev[milestoneId] || [];
      const updatedDeliverables = currentDeliverables.map(d =>
        d.id === deliverableId ? { ...d, ...updates, updatedAt: new Date() } : d
      );

      return {
        ...prev,
        [milestoneId]: updatedDeliverables
      };
    });
  };

  const deleteMilestoneDeliverable = (milestoneId: string, deliverableId: string) => {
    if (!checkPermission('projects.update')) {
      throw new Error('Insufficient permissions to delete deliverables');
    }

    setMilestoneDeliverables(prev => {
      const currentDeliverables = prev[milestoneId] || [];
      const updatedDeliverables = currentDeliverables.filter(d => d.id !== deliverableId);

      return {
        ...prev,
        [milestoneId]: updatedDeliverables
      };
    });
  };

  const reorderMilestoneDeliverables = (milestoneId: string, deliverables: MilestoneDeliverable[]) => {
    if (!checkPermission('projects.update')) {
      throw new Error('Insufficient permissions to reorder deliverables');
    }

    setMilestoneDeliverables(prev => ({
      ...prev,
      [milestoneId]: deliverables
    }));
  };

  const getProjectTasks = (projectId: string) => {
    return tasks.filter(task => task.projectId === projectId);
  };

  const getProjectMilestones = (projectId: string) => {
    return milestones.filter(milestone => milestone.projectId === projectId);
  };

  const getMilestoneTasks = (milestoneId: string) => {
    return tasks.filter(task => task.milestoneId === milestoneId);
  };

  const deleteTask = (id: string) => {
    if (!checkPermission('tasks.delete')) {
      throw new Error('Insufficient permissions to delete tasks');
    }
    removeBootstrapTask(id);
    setTasks(prev => prev.filter(t => t.id !== id));
    // Remove from milestone.tasks if present
    setMilestones(prev => prev.map(m => ({
      ...m,
      tasks: m.tasks.filter(t => t.id !== id)
    })));
  };

  // UI-facing mutations use one async contract in both environments. Demo
  // keeps the shared bootstrap store; Production waits for the authoritative
  // RPC result before updating the shared view.
  const createTaskAuthoritative = async (
    task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<Task> => {
    if (IS_DEMO_MODE) return createTask(task);
    if (!productionProjectPersistence) throw new Error('Production Task persistence is not configured.');
    const created = await productionProjectPersistence.createTask(task);
    setTasks(current => current.some(item => item.id === created.id) ? current : [...current, created]);
    return created;
  };

  const updateTaskAuthoritative = async (id: string, updates: Partial<Task>): Promise<Task> => {
    if (IS_DEMO_MODE) {
      updateTask(id, updates);
      const updated = _tasksStore.get().find(task => task.id === id);
      if (!updated) throw new Error('Task not found.');
      return updated;
    }
    if (!productionProjectPersistence) throw new Error('Production Task persistence is not configured.');
    const updated = await productionProjectPersistence.updateTask(id, updates);
    setTasks(current => current.map(task => task.id === id ? updated : task));
    setMilestones(current => current.map(milestone => ({
      ...milestone,
      tasks: milestone.tasks.map(task => task.id === id ? updated : task),
    })));
    return updated;
  };

  const deleteTaskAuthoritative = async (id: string): Promise<boolean> => {
    if (IS_DEMO_MODE) {
      deleteTask(id);
      return true;
    }
    if (!productionProjectPersistence) throw new Error('Production Task persistence is not configured.');
    const deleted = await productionProjectPersistence.deleteTask(id);
    if (deleted) {
      setTasks(current => current.filter(task => task.id !== id));
      setMilestones(current => current.map(milestone => ({
        ...milestone,
        tasks: milestone.tasks.filter(task => task.id !== id),
      })));
    }
    return deleted;
  };

  const createMilestoneAuthoritative = async (
    milestone: Omit<Milestone, 'id' | 'createdAt'>,
  ): Promise<Milestone> => {
    if (IS_DEMO_MODE) return createMilestone(milestone);
    if (!productionProjectPersistence) throw new Error('Production Milestone persistence is not configured.');
    const created = await productionProjectPersistence.createMilestone(milestone);
    setMilestones(current => current.some(item => item.id === created.id) ? current : [...current, created]);
    return created;
  };

  const updateMilestoneAuthoritative = async (id: string, updates: Partial<Milestone>): Promise<Milestone> => {
    if (IS_DEMO_MODE) {
      updateMilestone(id, updates);
      const updated = _milestonesStore.get().find(milestone => milestone.id === id);
      if (!updated) throw new Error('Milestone not found.');
      return updated;
    }
    if (!productionProjectPersistence) throw new Error('Production Milestone persistence is not configured.');
    const updated = await productionProjectPersistence.updateMilestone(id, updates);
    setMilestones(current => current.map(milestone => milestone.id === id
      ? { ...updated, tasks: milestone.tasks }
      : milestone));
    return updated;
  };

  const deleteMilestoneAuthoritative = async (id: string): Promise<boolean> => {
    if (IS_DEMO_MODE) {
      deleteMilestone(id);
      return true;
    }
    if (!productionProjectPersistence) throw new Error('Production Milestone persistence is not configured.');
    const deleted = await productionProjectPersistence.deleteMilestone(id);
    if (deleted) {
      setMilestones(current => current.filter(milestone => milestone.id !== id));
      setTasks(current => current.map(task => task.milestoneId === id
        ? { ...task, milestoneId: undefined, updatedAt: new Date() }
        : task));
    }
    return deleted;
  };

  return {
    projects,
    tasks,
    milestones,
    milestoneDeliverables,
    loading,
    error,
    refetch,
    createProject,
    updateProject,
    createTask,
    updateTask,
    deleteTask,
    createTaskAuthoritative,
    updateTaskAuthoritative,
    deleteTaskAuthoritative,
    createMilestone,
    updateMilestone,
    deleteMilestone,
    createMilestoneAuthoritative,
    updateMilestoneAuthoritative,
    deleteMilestoneAuthoritative,
    getProjectTasks,
    getProjectMilestones,
    getMilestoneTasks,
    getMilestoneDeliverables,
    addMilestoneDeliverable,
    updateMilestoneDeliverable,
    deleteMilestoneDeliverable,
    reorderMilestoneDeliverables
  };
}
